import React, { useState, useMemo } from "react";
import { 
  Filter, 
  X, 
  Trash2, 
  MapPin,
  Calendar,
  FileText,
  Wrench
} from "lucide-react";
import Dropdown from './Dropdown';

const ChatFilters = ({
  isOpen,
  onClose,
  filters,
  onFilterChange,
  onClearFilters,
  dropdownOptions,
  loadingDropdowns,
  activeFilters,
  sessionId,
  messages,
  connectionStatus,
  recordCount,
  getConnectionStatusColor,
  getConnectionStatusText,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Helper to filter dropdown options (case-insensitive)
  const getFilteredOptions = (options, search) => {
    if (!options) return [];
    if (!search || search.trim() === '') return options;
    const s = search.toLowerCase();
    return options.filter(opt => (opt || '').toLowerCase().includes(s));
  };

  // Memoized filtered sets to avoid recompute each render
  const filteredDropdownOptions = useMemo(() => ({
    report_type: getFilteredOptions(dropdownOptions.report_type, searchTerm),
    customer_name: getFilteredOptions(dropdownOptions.customer_name, searchTerm),
    site_name: getFilteredOptions(dropdownOptions.site_name, searchTerm),
    country_name: getFilteredOptions(dropdownOptions.country_name, searchTerm),
    part_number: getFilteredOptions(dropdownOptions.part_number, searchTerm),
    rig_model: getFilteredOptions(dropdownOptions.rig_model, searchTerm),
    rig_id: getFilteredOptions(dropdownOptions.rig_id, searchTerm),
  }), [dropdownOptions, searchTerm]);

  // Format date for display (dd-mm-yyyy)
  const formatDateForDisplay = (dateValue) => {
    if (!dateValue) return '';
    if (dateValue.includes('-') && dateValue.split('-')[0].length === 4) {
      const [year, month, day] = dateValue.split('-');
      return `${day}-${month}-${year}`;
    }
    return dateValue;
  };

  const validateDateRange = (fromDate, toDate) => {
    if (!fromDate || !toDate) return true;
    const from = new Date(fromDate);
    const to = new Date(toDate);
    return from <= to;
  };

  const removeFilter = (filterKey) => {
    if (filterKey === "date_from" || filterKey === "date_to") {
      onFilterChange("date_from", "");
      onFilterChange("date_to", "");
    } else {
      onFilterChange(filterKey, "");
    }
  };

  const handleClearAll = () => {
    onClearFilters();
    setSearchTerm('');
  };

  const enhancedActiveFilters = (activeFilters || []).map(([key, value]) => {
    if ((key === "date_from" || key === "date_to") && value) {
      return [key, formatDateForDisplay(value)];
    }
    return [key, value];
  });

  return (
    <div
      className={`
        ${isOpen ? "w-80" : "w-0"}
        ${isOpen ? "lg:translate-x-0" : "lg:translate-x-full"}
        ${
          isOpen && window.innerWidth < 1024
            ? "translate-x-0"
            : "translate-x-full lg:translate-x-0"
        }
        fixed lg:relative z-50 h-full right-0
        transition-all duration-300 ease-in-out
        bg-white border-l border-gray-200
        flex flex-col overflow-hidden
      `}
    >
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            {/* <Filter className="h-5 w-5 text-black" /> */}
            <h3 className="text-lg text-gray-900">Data Filters</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-n5 hover:text-gray-600 hover:bg-gray-100 transition-colors lg:hidden"
            title="Close filters"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <button
          onClick={handleClearAll}
          disabled={loadingDropdowns}
          className="w-full border border-gray-300 text-black py-2 px-3 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm flex items-center gap-2 text-left"
        >
          <Trash2 className="h-4 w-4 text-left" />
          Clear all filters
        </button>
        <p className="text-xs text-gray-500 mt-2 text-left">
          Search and apply filters to refine AI analysis
        </p>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-6">
          {/* Active Filters Display */}
          {enhancedActiveFilters.length > 0 && (
            <div className="p-3 bg-gray-50 border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm text-gray-900 flex items-center gap-1">
                  <Filter className="h-3 w-3" />
                  Active filters ({enhancedActiveFilters.length})
                </h4>
                <button onClick={handleClearAll} className="text-xs text-gray-600 hover:text-black">
                  Clear all
                </button>
              </div>
              <div className="space-y-2">
                {enhancedActiveFilters.map(([key, value]) => {
                  const displayKey = key === 'date_from' || key === 'date_to' ? `${key}_${value}` : key;
                  return (
                    <div key={displayKey} className="flex items-center justify-between bg-white px-3 py-2 border text-sm">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-gray-600">
                          {key === "date_from" ? "Date from" : key === "date_to" ? "Date to" : key.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                        </div>
                        <div className="text-sm text-gray-900 truncate">{value.length > 25 ? `${value.substring(0,25)}...` : value}</div>
                      </div>
                      <button onClick={() => removeFilter(key)} className="ml-2 text-n5 hover:text-gray-600">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Search field for dropdown options */}
          <div>
            {/* <label className="text-sm text-gray-700 mb-2 block">Filter Search</label> */}
            {/* <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-n5" size={16} />
              <input
                type="text"
                placeholder="Search dropdown options..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-10 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-black focus:border-electric-blue text-sm"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  title="Clear"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div> */}
            <p className="text-xs text-gray-500 mt-1 text-left">Type to filter dropdown options below</p>
          </div>

          {/* Report & Classification */}
          <div className="text-left">
            <h3 className="text-sm text-gray-900 mb-3 flex items-center gap-2">
              <FileText className="h-4 w-4 text-black" /> Report & Classification
            </h3>
            <Dropdown
              label="Report type"
              value={filters.report_type || ""}
              onChange={(value) => onFilterChange("report_type", value)}
              options={filteredDropdownOptions.report_type}
              loading={loadingDropdowns}
              placeholder="Select report type"
              disabled={loadingDropdowns}
            />
          </div>

          {/* Location & Customer */}
          <div>
            <h3 className="text-sm text-gray-900 mb-3 flex items-center gap-2">
              <MapPin className="h-4 w-4 text-black" /> Location & Customer
            </h3>
            <div className="space-y-2 text-left">
              <Dropdown
                label="Customer"
                value={filters.customer_name || ""}
                onChange={(value) => onFilterChange("customer_name", value)}
                options={filteredDropdownOptions.customer_name}
                loading={loadingDropdowns}
                placeholder="Select customer"
                disabled={loadingDropdowns}
              />
              <Dropdown
                label="Site name"
                value={filters.site_name || ""}
                onChange={(value) => onFilterChange("site_name", value)}
                options={filteredDropdownOptions.site_name}
                loading={loadingDropdowns}
                placeholder="Select site"
                disabled={loadingDropdowns}
              />
              <Dropdown
                label="Country"
                value={filters.country_name || ""}
                onChange={(value) => onFilterChange("country_name", value)}
                options={filteredDropdownOptions.country_name}
                loading={loadingDropdowns}
                placeholder="Select country"
                disabled={loadingDropdowns}
              />
            </div>
          </div>

          {/* Equipment & Parts */}
          <div>
            <h3 className="text-sm text-gray-900 mb-3 flex items-center gap-2">
              <Wrench className="h-4 w-4 text-black" /> Equipment & Parts
            </h3>
            <div className="space-y-2 text-left">
              <Dropdown
                label="Part number"
                value={filters.part_number || ""}
                onChange={(value) => onFilterChange("part_number", value)}
                options={filteredDropdownOptions.part_number}
                loading={loadingDropdowns}
                placeholder="Select part number"
                disabled={loadingDropdowns}
                
              />
              <Dropdown
                label="Rig model"
                value={filters.rig_model || ""}
                onChange={(value) => onFilterChange("rig_model", value)}
                options={filteredDropdownOptions.rig_model}
                loading={loadingDropdowns}
                placeholder="Select rig model"
                disabled={loadingDropdowns}
              />
              <Dropdown
                label="Rig ID"
                value={filters.rig_id || ""}
                onChange={(value) => onFilterChange("rig_id", value)}
                options={filteredDropdownOptions.rig_id}
                loading={loadingDropdowns}
                placeholder="Select rig ID"
                disabled={loadingDropdowns}
              />
            </div>
          </div>

          {/* Date Filters */}
          <div>
            <h3 className="text-sm text-gray-900 mb-3 flex items-center gap-2">
              <Calendar className="h-4 w-4 text-black" /> Time Span
            </h3>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm text-gray-700 mb-1  text-left" >From date</label>
                <input
                  type="date"
                  value={filters.date_from || ""}
                  onChange={(e) => onFilterChange('date_from', e.target.value)}
                  className="w-full border border-gray-300 px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-electric-blue"
                  disabled={loadingDropdowns}
                  max={filters.date_to || undefined}
                />
                {filters.date_from && <p className="text-xs text-gray-500 mt-1">{formatDateForDisplay(filters.date_from)}</p>}
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">To date</label>
                <input
                  type="date"
                  value={filters.date_to || ""}
                  onChange={(e) => onFilterChange('date_to', e.target.value)}
                  className="w-full border border-gray-300 px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-electric-blue"
                  disabled={loadingDropdowns}
                  min={filters.date_from || undefined}
                />
                {filters.date_to && <p className="text-xs text-gray-500 mt-1">{formatDateForDisplay(filters.date_to)}</p>}
              </div>
            </div>

            {((filters.date_from && !filters.date_to) || (!filters.date_from && filters.date_to)) && (
              <p className="text-xs text-gold-g3 mt-2">⚠️ Both dates required for filtering</p>
            )}
            {filters.date_from && filters.date_to && !validateDateRange(filters.date_from, filters.date_to) && (
              <p className="text-xs text-red-600 mt-2">❌ From date must be before or equal to To date</p>
            )}
          </div>

          {/* Session Info */}
          {/* <div className="p-3 bg-gray-50 border border-gray-200">
            <div className="text-xs text-gray-700 mb-2">Current AI Session</div>
            <div className="space-y-1 text-xs text-gray-600">
              <div>ID: {sessionId?.slice(-12)}</div>
              <div>Messages: {messages?.length || 0}</div>
              <div className="flex items-center gap-1">
                <div className={`w-2 h-2 ${getConnectionStatusColor()}`} />
                <span>Status: {getConnectionStatusText()}</span>
              </div>
              {recordCount > 0 && <div>Last Query: {recordCount.toLocaleString()} records</div>}
            </div>
          </div> */}

          {/* Help / Features */}
          {/* <div className="p-3 bg-gold-g1 border border-gold-g2 ">
            <h4 className="text-sm text-gold-g3">Ask features</h4>
            <ul className="text-xs text-gold-g3 mt-2 space-y-1">
              <li>• Filters focus AI analysis on specific data subsets</li>
              <li>• View raw data and SQL queries in responses</li>
              <li>• Export results to CSV for further analysis</li>
              <li>• Intent-based query processing with real-time insights</li>
              <li>• Dynamic chart and table visualizations</li>
            </ul>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default ChatFilters;